/**
 * Triggers a browser download for an in-memory Blob.
 *
 * Safari (macOS and iOS) does not reliably honour the `download` attribute
 * filename when the anchor points at a `blob:` URL: it frequently ignores the
 * given name/extension and saves the response as a generic ".zip" attachment,
 * which the user then cannot open. Reading the blob into a base64 `data:` URL
 * first makes Safari respect both the filename and the MIME type, so the file
 * downloads as the intended ".pdf" (or other type). Other browsers keep using
 * the cheaper object-URL path.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const isSafari =
    /^((?!chrome|chromium|crios|fxios|edg|android).)*safari/i.test(
      navigator.userAgent
    );

  if (isSafari) {
    const reader = new FileReader();
    reader.onloadend = () => {
      triggerDownload(reader.result as string, filename);
    };
    reader.readAsDataURL(blob);
    return;
  }

  const url = window.URL.createObjectURL(blob);
  triggerDownload(url, filename);
  window.URL.revokeObjectURL(url);
};

const triggerDownload = (href: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
